import React from "react";
import { formatCurrency } from "../../../utils/numbers";
import {
  isAvailable,
  valuationMessageFor,
  valuationAmountText,
} from "../../../services/valuation";
import { HeroSectionProps } from "../types";
import { YoYMetric } from "../../../services/finances/types";
import {
  HeroWrapper,
  HeroInner,
  HeroLeftPanel,
  HeroTopRow,
  HeroBottomRow,
  HeroCell,
  HeroBottomCell,
  HeroBottomCellDotLabel,
  HeroLabel,
  HeroLabelRow,
  HeroNetWorthValue,
  HeroValue,
  HeroSubtitle,
  HeroBadge,
  HeroDot,
  HeroSkeletonBox,
} from "../styles";
import { ManualInvestmentsHint } from "./ManualInvestmentsHint";

const yoyArrow = (metric: YoYMetric): string =>
  metric.dir === "up" ? "↑" : "↓";

const BOTTOM_ROW = [
  {
    id: "accounts",
    label: "BANK",
    color: "var(--color-info)",
    isNegative: false,
  },
  {
    id: "savings",
    label: "SAVINGS",
    color: "var(--color-purple)",
    isNegative: false,
  },
  {
    id: "investments",
    label: "INVESTMENTS",
    color: "var(--color-blue)",
    isNegative: false,
  },
  {
    id: "liabilities",
    label: "LIABILITIES",
    color: "var(--color-error)",
    isNegative: true,
  },
] as const;

const today = new Date()
  .toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
  .toUpperCase();

export const HeroSection: React.FC<HeroSectionProps> = React.memo(
  ({ isLoading, cards, yoy, hasManualInvestmentAccounts, valuation }) => {
    const getValue = (id: string): number | null => {
      const card = cards.find((candidate) => candidate.id === id);
      return card ? card.value : 0;
    };

    const netWorth = getValue("netWorth");
    const income = getValue("income");
    const expenses = getValue("expenses");
    const balance = getValue("balance");
    const accounts = getValue("accounts") ?? 0;
    const savings = getValue("savings") ?? 0;
    const investments = getValue("investments");
    const liabilities = getValue("liabilities");

    const hasCompletePortfolio = investments !== null && netWorth !== null;
    const totalAssets = hasCompletePortfolio
      ? accounts + savings + investments
      : null;
    const hasAssets = totalAssets !== null && totalAssets > 0;
    const allocationPct = (val: number) =>
      totalAssets === null || totalAssets === 0
        ? 0
        : Math.round((Math.abs(val) / totalAssets) * 100);

    if (isLoading) {
      return (
        <HeroWrapper data-testid="hero-skeleton">
          <HeroSkeletonBox />
        </HeroWrapper>
      );
    }

    const cardValues = { accounts, savings, investments, liabilities };
    const manualInvestmentsHint = hasManualInvestmentAccounts ? (
      <ManualInvestmentsHint />
    ) : null;

    return (
      <HeroWrapper role="region" aria-label="Financial overview">
        <HeroInner>
          <HeroLeftPanel>
            <HeroLabel>NET WORTH · AS OF {today}</HeroLabel>
            <HeroNetWorthValue data-testid="hero-networth-value">
              {valuationAmountText({ value: netWorth, valuation })}
            </HeroNetWorthValue>
            {netWorth !== null && yoy?.netWorth && (
              <HeroBadge
                $negative={yoy.netWorth.dir === "down"}
                data-testid="hero-networth-badge"
              >
                {yoyArrow(yoy.netWorth)} {yoy.netWorth.pct}% vs last year
              </HeroBadge>
            )}
            {netWorth !== null &&
              yoy !== null &&
              yoy.netWorth === null &&
              !isAvailable(yoy.netWorthValuation) && (
                <HeroSubtitle data-testid="hero-networth-yoy-note">
                  No comparison vs last year ·{" "}
                  {valuationMessageFor(yoy.netWorthValuation)}
                </HeroSubtitle>
              )}
          </HeroLeftPanel>

          <HeroTopRow>
            <HeroCell data-testid="hero-income-cell">
              <HeroLabelRow>
                <HeroLabel>INCOME</HeroLabel>
                {manualInvestmentsHint}
              </HeroLabelRow>
              <HeroValue $color="var(--color-success)">
                {formatCurrency(income)}
              </HeroValue>
              {yoy?.income && (
                <HeroSubtitle>
                  {yoyArrow(yoy.income)} {yoy.income.pct}% vs last year
                </HeroSubtitle>
              )}
            </HeroCell>
            <HeroCell $divider data-testid="hero-expenses-cell">
              <HeroLabel>EXPENSES</HeroLabel>
              <HeroValue $color="var(--color-error)">
                {formatCurrency(expenses)}
              </HeroValue>
              {yoy?.expenses && (
                <HeroSubtitle>
                  {yoyArrow(yoy.expenses)} {yoy.expenses.pct}% vs last year
                </HeroSubtitle>
              )}
            </HeroCell>
            <HeroCell $divider data-testid="hero-balance-cell">
              <HeroLabel>BALANCE</HeroLabel>
              <HeroValue>{formatCurrency(balance)}</HeroValue>
              {yoy?.balance && (
                <HeroSubtitle>
                  {yoyArrow(yoy.balance)} {yoy.balance.pct}% vs last year
                </HeroSubtitle>
              )}
            </HeroCell>
          </HeroTopRow>
        </HeroInner>

        <HeroBottomRow>
          {BOTTOM_ROW.map(({ id, label, color, isNegative }, index) => {
            const value = cardValues[id];
            const allocationLabel = isNegative
              ? "Debt as share of total assets"
              : "Share of total assets";
            const pct = value === null ? 0 : allocationPct(value);
            const allocationSubtitle =
              hasAssets && value !== null ? (
                <HeroSubtitle
                  title={allocationLabel}
                  aria-label={`${pct}% — ${allocationLabel}`}
                >
                  {pct}%
                </HeroSubtitle>
              ) : null;
            return (
              <HeroBottomCell
                key={id}
                $first={index === 0}
                data-testid={`hero-${id}-cell`}
              >
                <HeroBottomCellDotLabel>
                  <HeroDot $color={color} />
                  <HeroLabel>{label}</HeroLabel>
                </HeroBottomCellDotLabel>
                <HeroValue
                  $small
                  $color={isNegative ? "var(--color-error)" : undefined}
                >
                  {valuationAmountText({
                    value,
                    valuation: id === "investments" ? valuation : null,
                  })}
                </HeroValue>
                {allocationSubtitle}
              </HeroBottomCell>
            );
          })}
        </HeroBottomRow>
      </HeroWrapper>
    );
  },
);

HeroSection.displayName = "HeroSection";
